import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth, formatApiError } from "@/context/AuthContext";

export default function LoginPage() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (user) return <Navigate to="/" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Hoş geldiniz");
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      <div className="hidden lg:block relative overflow-hidden bg-zinc-900">
        <img
          src="https://images.unsplash.com/photo-1635111057505-3b7dcc2b72fb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwzfHxyZWFsJTIwZXN0YXRlJTIwcHJvcGVydHklMjBhYnN0cmFjdHxlbnwwfHx8fDE3ODUwMzM1NTh8MA&ixlib=rb-4.1.0&q=85"
          alt="Ekspertiz"
          className="absolute inset-0 h-full w-full object-cover opacity-80"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-zinc-950/70 via-zinc-900/40 to-transparent" />
        <div className="relative z-10 p-12 h-full flex flex-col justify-between text-white">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 grid place-items-center bg-white text-zinc-900 rounded-sm">
              <MapPin className="h-5 w-5" strokeWidth={2.5} />
            </div>
            <span className="text-xl tracking-tight font-semibold">Kolay Arşivim</span>
          </div>
          <div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              Sahaya çık, <br /> parsele işaretle.
            </h1>
            <p className="mt-6 text-base text-zinc-200 max-w-md">
              Şehir, mahalle, ada, parsel bilgilerini seçin; ziyaret ettiğiniz taşınmazı harita üzerine işaretleyin ve tüm ekspertiz geçmişinizi tek panelden yönetin.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-8 py-12">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <h2 className="text-3xl font-bold tracking-tight text-zinc-900">Giriş Yap</h2>
            <p className="mt-2 text-sm text-zinc-500">Hesabınızla ekspertiz panelinize erişin.</p>
          </div>
          <form className="space-y-5" onSubmit={handleSubmit} data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">E-posta</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                placeholder="ornek@parsel.com"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Şifre</Label>
                <Link
                  to="/sifremi-unuttum"
                  className="text-xs text-zinc-500 hover:text-zinc-900 underline underline-offset-2"
                  data-testid="forgot-password-link"
                >
                  Şifremi unuttum
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                placeholder="••••••••"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm"
              data-testid="login-submit-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Giriş Yap"}
            </Button>
          </form>
          <p className="mt-6 text-sm text-zinc-500">
            Hesabınız yok mu?{" "}
            <Link to="/kayit" className="text-zinc-900 font-medium underline underline-offset-4" data-testid="go-register-link">
              Kayıt olun
            </Link>
          </p>
          <p className="mt-8 text-[11px] text-zinc-400 leading-relaxed">
            <Link to="/kullanim" className="underline hover:text-zinc-900">Kullanım Şartları</Link>
            {" · "}
            <Link to="/gizlilik" className="underline hover:text-zinc-900">Gizlilik</Link>
            {" · "}
            <Link to="/aydinlatma" className="underline hover:text-zinc-900">Aydınlatma</Link>
            {" · "}
            <Link to="/cerez" className="underline hover:text-zinc-900">Çerez</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
